import { useState } from "react";

import { faSearch, faUsers } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Stack, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import invariant from "ts-invariant";

import { useGetConversations } from "api/services/messaging/hooks/useGetConversations";
import { useProfileContext } from "contexts/ProfileContext";
import { useSavedInputsContext } from "contexts/SavedInputsContext";
import { SkeletonLayout } from "shared/atoms/SkeletonLayout";
import { Search } from "shared/molecules/Search";
import { DARK_BLUE } from "theme/colors";

import { ConversationItem } from "./ConversationItem";
import { sortConversations } from "./utils";

const initialSearchTerm = "";

export const ConversationList: React.FC = () => {
  const { t } = useTranslation();
  const { register, setValue } = useForm<{ search: string }>();
  const { profile } = useProfileContext();
  const { savedMessages } = useSavedInputsContext();
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);

  invariant(profile);

  const { data, isLoading } = useGetConversations(profile.id, { refetchInterval: 30000 });
  const conversations = data?.conversations || [];
  const filteredConversations = conversations
    .filter(conversation => {
      if (!searchTerm) return true;

      const peerName = `${conversation.peer.first_name || ""} ${conversation.peer.last_name || ""}`
        .trim()
        .toLowerCase();
      const lastMessageBody = conversation.last_message?.body?.toLowerCase() || "";
      const pinnedMessageBody = conversation.pinned_messages[0]?.body?.toLowerCase() || "";
      const search = searchTerm.toLowerCase();

      return peerName.includes(search) || lastMessageBody.includes(search) || pinnedMessageBody.includes(search);
    })
    .sort(sortConversations);

  return (
    <Stack
      data-testid="conversation-list"
      sx={{
        width: "100%",
        backgroundColor: "#fff",
      }}
    >
      <StyledSearch
        icon={() => <FontAwesomeIcon icon={faSearch} size="sm" />}
        {...register("search")}
        name="conversationSearch"
        reset={() => {
          setValue("search", "");
          setSearchTerm("");
        }}
        placeholder={t("common.search")}
        search={searchTerm}
        onChange={async e => {
          setSearchTerm(e.target.value);
        }}
        initialValue={initialSearchTerm}
      />
      <Stack sx={{ flex: 1, borderRight: `1px solid ${DARK_BLUE[10]}`, overflowY: "auto" }}>
        {isLoading &&
          Array.from({ length: 12 }).map((_, index) => (
            <SkeletonLayout
              // eslint-disable-next-line react/no-array-index-key
              key={`skeleton-${index}`}
              layout={{
                type: "row",
                alignItems: "center",
                spacing: 2,
                padding: "12px 16px",
                style: { borderBottom: `1px solid ${DARK_BLUE[10]}` },
                children: [
                  { type: "skeleton", variant: "circular", width: 40, height: 40 },
                  {
                    type: "column",
                    spacing: 1,
                    children: [
                      { type: "skeleton", variant: "text", typographyVariant: "subtitle1", width: "70%" },
                      { type: "skeleton", variant: "text", typographyVariant: "label", width: "100%" },
                    ],
                  },
                ],
              }}
            />
          ))}
        {!isLoading && filteredConversations.length === 0 && (
          <Stack spacing={2} sx={{ alignItems: "center", justifyContent: "center", marginTop: "36px" }}>
            <FontAwesomeIcon icon={faUsers} fontSize="40px" color={DARK_BLUE[30]} />
            <Typography variant="subtitle1" color={DARK_BLUE[40]}>
              {t("messages.no_patients")}
            </Typography>
          </Stack>
        )}
        {!isLoading &&
          filteredConversations.length > 0 &&
          filteredConversations.map((conversation, index) => (
            <ConversationItem
              // eslint-disable-next-line react/no-array-index-key
              key={`${conversation.peer.id}-${index}`}
              hasDraft={!!savedMessages[conversation.peer.id]}
              has_unread_messages={conversation.has_unread_messages}
              last_message={conversation.last_message}
              peer={conversation.peer}
              pinned_messages={conversation.pinned_messages}
            />
          ))}
      </Stack>
    </Stack>
  );
};

const StyledSearch = styled(Search)`
  border-radius: 16px 16px 0 0;

  ${({ theme }) => theme.aboveBreakpoint} {
    border-radius: 16px 0 0 0;
  }
`;
